import { radioStations, type RadioStation } from '@/mocks/radios';

// ─── Interfaz unificada de Discovery ───
export interface DiscoveryRadio {
  id: number;
  name: string;
  slug: string;
  frequency: string;
  category: string;
  city: string;
  state: string;
  logoUrl: string;
  isLive: boolean;
  listeners: number;
  radioScore: number;
  rating: number;
  followers: number;
  description: string;
  createdAt?: string;
  trendingScore?: number;
  trendChange?: number;
  growthPercent?: string;
  newListeners?: number;
  activityLevel?: 'muy_activa' | 'activa' | 'moderada' | 'pausada';
}

function buildDiscoveryRadio(station: RadioStation, extras?: Partial<DiscoveryRadio>): DiscoveryRadio {
  return {
    id: station.id,
    name: station.name,
    slug: station.slug,
    frequency: station.frequency,
    category: station.category,
    city: station.city,
    state: station.state,
    logoUrl: station.logoUrl,
    isLive: station.isLive,
    listeners: station.listeners,
    radioScore: Math.round(station.radioScore),
    rating: station.rating,
    followers: Math.round(station.listeners * 4),
    description: station.description,
    ...extras,
  };
}

// ─── Estaciones fantasmas (solo en discovery) ───
const phantomStations: Record<number, DiscoveryRadio> = {
  201: {
    id: 201, name: 'Radio Brisa Marina', slug: 'radio-brisa-marina', frequency: '92.30 RFW',
    category: 'Pop', city: 'Cancún', state: 'Quintana Roo',
    logoUrl: 'https://readdy.ai/api/search-image?query=Beach%20vibes%20radio%20station%20logo%20with%20ocean%20wave%20and%20palm%20tree%2C%20circular%20design%20turquoise%20and%20coral%20colors%2C%20modern%20tropical%20broadcast%20emblem%2C%20relaxed%20coastal%20radio%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=disc-new-1&orientation=squarish',
    isLive: true, listeners: 1240, radioScore: 72, rating: 4.2, followers: 3400,
    description: 'Sonidos frescos desde el Caribe mexicano. Música, playa y buen vibe.',
    createdAt: '2026-05-28', trendingScore: 68, trendChange: 3,
  },
  202: {
    id: 202, name: 'Indie Frequency FM', slug: 'indie-frequency-fm', frequency: '105.80 RFW',
    category: 'Rock', city: 'Buenos Aires', state: 'CABA',
    logoUrl: 'https://readdy.ai/api/search-image?query=Indie%20rock%20radio%20station%20logo%20with%20vintage%20cassette%20tape%20and%20geometric%20shapes%2C%20circular%20design%20warm%20amber%20and%20teal%20colors%2C%20alternative%20broadcast%20emblem%2C%20underground%20music%20radio%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=disc-new-2&orientation=squarish',
    isLive: true, listeners: 2180, radioScore: 76, rating: 4.4, followers: 5600,
    description: 'El hogar del indie y el rock alternativo. Descubrí música nueva todos los días.',
    createdAt: '2026-05-22', trendingScore: 74, trendChange: 5,
  },
  203: {
    id: 203, name: 'Radio Sabores Latinos', slug: 'radio-sabores-latinos', frequency: '88.60 RFW',
    category: 'Regional Mexicana', city: 'Medellín', state: 'Antioquia',
    logoUrl: 'https://readdy.ai/api/search-image?query=Latin%20flavors%20radio%20station%20logo%20with%20tropical%20fruits%20and%20musical%20notes%2C%20circular%20design%20vibrant%20orange%20and%20lime%20green%20colors%2C%20festive%20broadcast%20emblem%2C%20Latin%20rhythm%20radio%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=disc-new-3&orientation=squarish',
    isLive: false, listeners: 890, radioScore: 68, rating: 4.0, followers: 2100,
    description: 'Una mezcla explosiva de ritmos latinos: cumbia, salsa, merengue y más.',
    createdAt: '2026-05-18', trendingScore: 62, trendChange: 8,
  },
  204: {
    id: 204, name: 'Conexión Deportiva Web', slug: 'conexion-deportiva-web', frequency: '130.50 RFW',
    category: 'Deportes', city: 'Santiago', state: 'Metropolitana',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20dynamic%20swoosh%20and%20ball%20icon%2C%20circular%20design%20bold%20crimson%20and%20white%20colors%2C%20energetic%20broadcast%20emblem%2C%20Latin%20American%20sports%20radio%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=disc-new-4&orientation=squarish',
    isLive: true, listeners: 3150, radioScore: 78, rating: 4.5, followers: 8900,
    description: 'La nueva voz del deporte latinoamericano. Análisis, debate y pasión.',
    createdAt: '2026-05-12', trendingScore: 82, trendChange: 6,
  },
};

// ─── Secciones de descubrimiento ───

export const newRadios: DiscoveryRadio[] = [
  phantomStations[201], phantomStations[202], phantomStations[203], phantomStations[204],
];

export const trendingRadios: DiscoveryRadio[] = [
  buildDiscoveryRadio(radioStations.find((s) => s.id === 3)!, { trendingScore: 98, trendChange: 3 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 7)!, { trendingScore: 97, trendChange: 2 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 5)!, { trendingScore: 95, trendChange: -1 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 12)!, { trendingScore: 91, trendChange: 5 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 9)!, { trendingScore: 88, trendChange: 4 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 2)!, { trendingScore: 78, trendChange: 0 }),
];

export const nearTopRadios: DiscoveryRadio[] = [
  buildDiscoveryRadio(radioStations.find((s) => s.id === 9)!, { trendingScore: 88, growthPercent: '+24%', newListeners: 1520 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 11)!, { trendingScore: 82, growthPercent: '+18%', newListeners: 890 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 10)!, { trendingScore: 58, growthPercent: '+12%', newListeners: 340 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 6)!, { trendingScore: 62, growthPercent: '+9%', newListeners: 210 }),
];

export const mostActiveRadios: DiscoveryRadio[] = [
  buildDiscoveryRadio(radioStations.find((s) => s.id === 3)!, { trendingScore: 98, activityLevel: 'muy_activa' }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 5)!, { trendingScore: 95, activityLevel: 'muy_activa' }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 7)!, { trendingScore: 97, activityLevel: 'muy_activa' }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 12)!, { trendingScore: 91, activityLevel: 'muy_activa' }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 1)!, { trendingScore: 92, activityLevel: 'muy_activa' }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 2)!, { trendingScore: 78, activityLevel: 'activa' }),
];

export const bestScoreRadios: DiscoveryRadio[] = [
  buildDiscoveryRadio(radioStations.find((s) => s.id === 2)!, { trendingScore: 78 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 7)!, { trendingScore: 97 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 3)!, { trendingScore: 98 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 1)!, { trendingScore: 92 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 11)!, { trendingScore: 82 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 12)!, { trendingScore: 91 }),
];

export const recommendedRadios: DiscoveryRadio[] = [
  buildDiscoveryRadio(radioStations.find((s) => s.id === 1)!, { trendingScore: 92 }),
  phantomStations[204],
  phantomStations[202],
  buildDiscoveryRadio(radioStations.find((s) => s.id === 5)!, { trendingScore: 95 }),
  buildDiscoveryRadio(radioStations.find((s) => s.id === 4)!, { trendingScore: 85 }),
  phantomStations[201],
];

// ─── Info de secciones ───
export interface DiscoverySection {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  accentClass: string;
  data: DiscoveryRadio[];
}

export const discoverySections: DiscoverySection[] = [
  {
    id: 'nuevas', title: 'Nuevas Radios',
    subtitle: 'Las emisoras que acaban de unirse a la plataforma. Descubrilas antes que nadie.',
    icon: 'ri-sparkling-line', accentClass: 'bg-emerald-100 text-emerald-700', data: newRadios,
  },
  {
    id: 'tendencia', title: 'Tendencia',
    subtitle: 'Las estaciones que están marcando la conversación ahora mismo.',
    icon: 'ri-fire-line', accentClass: 'bg-amber-100 text-amber-700', data: trendingRadios,
  },
  {
    id: 'cerca-de-subir', title: 'Cerca de Subir',
    subtitle: 'Radios con mayor crecimiento. Las próximas estrellas de la plataforma.',
    icon: 'ri-rocket-line', accentClass: 'bg-sky-100 text-sky-700', data: nearTopRadios,
  },
  {
    id: 'mas-activas', title: 'Más Activas',
    subtitle: 'Las radios con mayor nivel de actividad: lives, publicaciones y comunidad.',
    icon: 'ri-flashlight-line', accentClass: 'bg-rose-100 text-rose-700', data: mostActiveRadios,
  },
  {
    id: 'mejor-score', title: 'Mejor Radio Score',
    subtitle: 'Ranking por calidad general. El Radio Score mide oyentes, engagement y consistencia.',
    icon: 'ri-trophy-line', accentClass: 'bg-primary-100 text-primary-700', data: bestScoreRadios,
  },
  {
    id: 'recomendadas', title: 'Recomendadas',
    subtitle: 'Selección personalizada basada en tus gustos y tendencias de la comunidad.',
    icon: 'ri-thumb-up-line', accentClass: 'bg-violet-100 text-violet-700', data: recommendedRadios,
  },
];